import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath, OnboardingFlowMode } from './onboardingRoutes';
import { launchSuiteApp } from '../../utils/launchSuiteApp';
import { AppInfoTooltip } from '../../components/AppInfoTooltip';
import { safeContinuePath } from '../../utils/safeContinuePath';
import { AppLogo } from './AppLogo';
import { AppsDisclosure, COLLAPSE_APPS_ABOVE } from './AppsDisclosure';
import { OutcomesSummary } from './OutcomesSummary';
import { appBenefit } from './appBenefits';

/**
 * CompletePage — the Workspace Assembly experience (PR-A2).
 *
 * The final onboarding step is a state machine that turns the user's
 * selected outcomes into a real, provisioned workspace. It reflects only
 * REAL provisioning signals — there are no faked phases and no pretend
 * workflow/automation sections (the backend has no data for those yet).
 *
 * Phases:
 *  loading           — fetching onboarding state
 *  resolving         — fetching the resolved plan (pricing-summary)
 *  ready-to-assemble — plan shown; user confirms
 *  provisioning      — POST /api/onboarding/complete in flight
 *  verifying         — GET /api/workspace/current confirming the result
 *  ready             — workspace provisioned; launcher shown
 *  degraded          — workspace not yet reflecting apps; retry
 *  failed-recoverable— provisioning failed; retry
 *
 * Backend step machine and ONBOARDING_FLOWS are unchanged — this is a
 * frontend reshape of the existing `complete` step.
 */

type AssemblyPhase =
  | 'loading'
  | 'resolving'
  | 'ready-to-assemble'
  | 'provisioning'
  | 'verifying'
  | 'ready'
  | 'degraded'
  | 'failed-recoverable';

interface PlanOutcome {
  code: string;
  displayName: string;
}

interface PlanApp {
  appId: string;
  displayName: string;
  /**
   * Phase 2.x-c PR-C1 — Application Registry v2 copy, mode-resolved by
   * /api/onboarding/pricing-summary (PR-B). Optional so legacy payloads
   * degrade to displayName-only via the AppInfoTooltip fallback.
   */
  subtitle?: string;
  description?: string;
  /**
   * Phase 2.x-c PR-C3b — accepted on the type so the pricing-summary
   * payload passes through cleanly. Complete.tsx does NOT render
   * traceability (out of PR-C3b scope; provisioning/launch stays
   * price-free). If a later PR wants to show "from {outcome}" here,
   * the data is already available.
   */
  activatedByOutcomes?: string[];
  /** The app's logo — the same file the app switcher shows. */
  iconUrl?: string;
}

interface LaunchableApp {
  appId: string;
  displayName: string;
  /**
   * Phase 2.x-c PR-C1 — Application Registry v2 copy, mode-resolved by
   * /api/workspace/current (PR-A). Same optional-with-fallback contract
   * as PlanApp.
   */
  subtitle?: string;
  description?: string;
  /** The app's logo — the same file the app switcher shows. */
  iconUrl?: string;
  launchUrl: string | null;
  launchable: boolean;
  /** H-2A: true → launch via the OIDC flow; false/absent → bare link. */
  oidcLaunch?: boolean;
}

const MODE_COPY: Record<OnboardingFlowMode, {
  assembleHeading: string;
  assembleSub: string;
  workingHeading: string;
  workingSub: string;
  readyHeading: string;
  readySub: string;
  confirmCta: string;
}> = {
  business: {
    assembleHeading: 'Assemble your business workspace',
    assembleSub:
      "Here's the operating system we'll switch on for your clients, operations, growth, and team execution.",
    workingHeading: 'Activating your business workspace',
    workingSub: 'Switching on your apps and getting your workspace ready…',
    readyHeading: 'Your business workspace is ready',
    readySub: 'Everything you selected is now switched on for your team.',
    confirmCta: 'Assemble my workspace',
  },
  personal: {
    assembleHeading: 'Set up your space',
    assembleSub:
      "Here's everything we'll set up to help you organize your home, goals, and life.",
    workingHeading: 'Setting up your space',
    workingSub: 'Getting your apps and your space ready…',
    readyHeading: 'Your space is ready',
    readySub: 'Everything you chose is set up and ready for you.',
    confirmCta: 'Set up my space',
  },
};

/**
 * The line under an app's name on the last step: its purpose, in plain words
 * ("Home — Family calendar, chores and budget"). This is where apps are
 * introduced properly; an app with nothing to say shows its name alone.
 */
function PurposeLine({ app, mode }: { app: PlanApp | LaunchableApp; mode: OnboardingFlowMode | null }) {
  const purpose = appBenefit(app, mode);
  return purpose ? <span className="mt-0.5 block text-xs text-gray-500">{purpose}</span> : null;
}

/**
 * Only OIDC callback URLs are honored as post-completion redirects. C0: the
 * query value must also pass safeContinuePath (no dot segments, '//', '\\',
 * raw or encoded), and the original validated string is what is followed.
 */
function isValidOidcContinueUrl(url: string): boolean {
  return url.startsWith('/api/oidc/') && safeContinuePath(url) === url;
}

export function CompletePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  const [phase, setPhase] = useState<AssemblyPhase>('loading');
  const [mode, setMode] = useState<OnboardingFlowMode | null>(null);
  const [workspaceType, setWorkspaceType] =
    useState<'personal' | 'team' | 'enterprise' | null>(null);
  const [outcomes, setOutcomes] = useState<PlanOutcome[]>([]);
  const [planApps, setPlanApps] = useState<PlanApp[]>([]);
  const [trialDays, setTrialDays] = useState(0);
  const [workspaceApps, setWorkspaceApps] = useState<LaunchableApp[]>([]);
  const [defaultApp, setDefaultApp] = useState<LaunchableApp | null>(null);
  const [error, setError] = useState('');
  // A long app list starts collapsed behind a one-line summary.
  const [appsExpanded, setAppsExpanded] = useState(false);
  const [yourAppsExpanded, setYourAppsExpanded] = useState(false);

  /** Verify the provisioned workspace via the canonical /api/workspace/current. */
  const verifyWorkspace = useCallback(async () => {
    setPhase('verifying');
    setError('');
    try {
      const res = await fetch('/api/workspace/current', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        setPhase('degraded');
        return;
      }
      if (data.workspace?.mode === 'business' || data.workspace?.mode === 'personal') {
        setMode(data.workspace.mode);
      }
      if (data.status === 'provisioned') {
        // Preserve subtitle + description from /api/workspace/current
        // (already mode-resolved by PR-A). AppInfoTooltip handles the
        // absence of either field cleanly.
        const rawApps = Array.isArray(data.apps) ? data.apps : [];
        setWorkspaceApps(
          rawApps.map((a: LaunchableApp) => ({
            appId: a.appId,
            displayName: a.displayName,
            subtitle: a.subtitle,
            description: a.description,
            iconUrl: a.iconUrl,
            launchUrl: a.launchUrl,
            launchable: a.launchable,
            oidcLaunch: a.oidcLaunch,
          })),
        );
        setDefaultApp(data.defaultApp ?? null);
        setPhase('ready');
      } else {
        // `empty` (no apps yet), or any other non-provisioned status — the
        // workspace exists but is not ready to launch. Recoverable.
        setPhase('degraded');
      }
    } catch {
      setPhase('degraded');
    }
  }, []);

  /** Load the resolved plan (outcomes + apps) for the pre-provision preview. */
  const loadPlan = useCallback(async () => {
    setPhase('resolving');
    setError('');
    try {
      const res = await fetch('/api/onboarding/pricing-summary', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        setError("We couldn't prepare your workspace. Your selections are safe — please try again.");
        setPhase('failed-recoverable');
        return;
      }
      const planMode: OnboardingFlowMode = data.mode === 'personal' ? 'personal' : 'business';
      setMode(planMode);

      // No usable outcomes — never show an empty assembly. Recover to the
      // outcomes step (R3 missing-outcomes contract).
      if (data.status === 'missing-outcomes') {
        navigate(onboardingPath(planMode, 'outcomes', continueUrl));
        return;
      }

      setOutcomes(
        (data.selectedOutcomes ?? []).map((o: PlanOutcome) => ({
          code: o.code,
          displayName: o.displayName,
        })),
      );
      // Preserve subtitle + description from /api/onboarding/pricing-summary
      // (mode-resolved by PR-B). The ready-to-assemble chip and the
      // provisioning "Activating your apps" list both consume this.
      setPlanApps(
        (data.prospectiveApps ?? data.apps ?? []).map((a: PlanApp) => ({
          appId: a.appId,
          displayName: a.displayName,
          subtitle: a.subtitle,
          description: a.description,
          iconUrl: a.iconUrl,
        })),
      );
      setTrialDays(data.pricing?.trialDays ?? 0);
      setPhase('ready-to-assemble');
    } catch {
      setError('Network error. Please try again.');
      setPhase('failed-recoverable');
    }
  }, [navigate, continueUrl]);

  // Mount: load onboarding state. An already-complete workspace skips
  // confirm/provision and goes straight to verification.
  useEffect(() => {
    async function loadInitial() {
      try {
        const res = await fetch('/api/onboarding/state', { credentials: 'include' });
        const data = await res.json();
        if (!res.ok || !data?.state) {
          setError("We couldn't load your workspace. Please try again.");
          setPhase('failed-recoverable');
          return;
        }
        const st = data.state;
        if (st.onboardingMode === 'business' || st.onboardingMode === 'personal') {
          setMode(st.onboardingMode);
        }
        if (st.workspaceType) setWorkspaceType(st.workspaceType);

        if (st.isComplete) {
          // Already provisioned — verify, never re-POST complete.
          await verifyWorkspace();
        } else {
          await loadPlan();
        }
      } catch {
        setError('Network error. Please try again.');
        setPhase('failed-recoverable');
      }
    }
    loadInitial();
  }, [verifyWorkspace, loadPlan]);

  /** Confirm → POST /api/onboarding/complete (the one real provisioning call). */
  const confirmAssembly = useCallback(async () => {
    setPhase('provisioning');
    setError('');
    try {
      const res = await fetch('/api/onboarding/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        await verifyWorkspace();
        return;
      }
      // T-7: the resolved plan has no usable price, so Central refused to
      // provision it (409). That is NOT "already complete" — say so.
      if (data?.code === 'PLAN_PRICE_UNAVAILABLE') {
        setError(data?.error || 'This plan is not available for online sign-up. Contact us to continue.');
        setPhase('failed-recoverable');
        return;
      }
      // 409 ALREADY_COMPLETE is not a failure — the workspace is provisioned.
      if (res.status === 409 || data?.code === 'ALREADY_COMPLETE') {
        await verifyWorkspace();
        return;
      }
      if (data?.code === 'EMAIL_NOT_VERIFIED') {
        // Carry the journey into the verify screen: it forwards return_to
        // into any link it sends, and "Continue to sign in" resumes there.
        // /start/{mode} resumes this workspace at its recorded step (the
        // complete step) — it never creates another workspace.
        // Without this the verified user signed in with no continuation and
        // landed on the /onboarding/new Business/Personal chooser.
        //
        // ONB-05: the mode comes from THIS workspace — its recorded mode, else
        // its type — never a hard-coded Business (which would route a personal
        // workspace into the business flow). Unknown → /home, which resolves
        // the workspace's own state.
        const resumeMode: OnboardingFlowMode | null =
          mode ?? (workspaceType === 'personal' ? 'personal' : workspaceType ? 'business' : null);
        const resumeBase = resumeMode ? `/start/${resumeMode}` : '/home';
        const resume = `${resumeBase}${
          continueUrl ? `?continue=${encodeURIComponent(continueUrl)}` : ''
        }`;
        navigate(`/auth/verify-email?return_to=${encodeURIComponent(resume)}`);
        return;
      }
      setError(data?.error || 'We could not finish assembling your workspace.');
      setPhase('failed-recoverable');
    } catch {
      setError('Network error. Please try again.');
      setPhase('failed-recoverable');
    }
  }, [navigate, verifyWorkspace, mode, workspaceType, continueUrl]);

  const handleBack = async () => {
    try {
      await fetch('/api/onboarding/back', { method: 'POST', credentials: 'include' });
    } catch {
      // Ignore errors on back navigation.
    }
    if (mode === 'personal') {
      navigate(onboardingPath('personal', 'pricing', continueUrl));
    } else if (workspaceType === 'personal') {
      navigate(onboardingPath('business', 'review', continueUrl));
    } else {
      navigate(onboardingPath('business', 'invites', continueUrl));
    }
  };

  const goHome = () => navigate('/home');

  const layoutMode: 'business' | 'personal' | null = mode;
  const copy = MODE_COPY[mode ?? 'business'];

  // ── Working states (loading / resolving / provisioning / verifying) ──
  if (phase === 'loading' || phase === 'resolving') {
    return (
      <OnboardingLayout currentStep="complete" workspaceType={workspaceType} onboardingMode={layoutMode}>
        <div className="rounded-3xl bg-white p-10 text-center shadow-2xl shadow-black/40 ring-1 ring-black/5">
          <div className="mx-auto h-9 w-9 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-500">Preparing your workspace…</p>
        </div>
      </OnboardingLayout>
    );
  }

  if (phase === 'provisioning' || phase === 'verifying') {
    return (
      <OnboardingLayout currentStep="complete" workspaceType={workspaceType} onboardingMode={layoutMode} wide>
        <div className="overflow-hidden rounded-3xl bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5">
          <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-900 px-4 py-6 text-center text-white sm:px-10 sm:py-10">
            <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
            <div className="relative">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15 sm:h-16 sm:w-16">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-300 border-t-transparent"></div>
              </div>
              <h1 className="mt-3 text-xl font-semibold tracking-tight sm:mt-5 sm:text-3xl">{copy.workingHeading}</h1>
              <p className="mx-auto mt-1.5 max-w-xl text-sm text-slate-300 sm:mt-2 sm:text-base">{copy.workingSub}</p>
            </div>
          </div>
          {planApps.length > 0 && (
            <div className="p-4 sm:p-8">
              <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 sm:mb-3 sm:text-xs">
                Activating your apps
              </h2>
              {/*
                Each app's purpose beneath its name, so the user sees the
                value being switched on, not just a bare name. Description
                surfaces via AppInfoTooltip. Falls back to the name alone
                when the app has no copy.
              */}
              <ul className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                {planApps.map((app) => (
                  <li
                    key={app.appId}
                    className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/70 p-3"
                  >
                    <AppLogo appId={app.appId} displayName={app.displayName} iconUrl={app.iconUrl} />
                    <AppInfoTooltip
                      description={app.description}
                      className={`block min-w-0 flex-1 text-left leading-tight ${
                        app.description
                          ? 'rounded focus:outline-none focus:ring-2 focus:ring-emerald-400'
                          : ''
                      }`}
                      testId={`activating-app-${app.appId}`}
                    >
                      <span className="block text-sm font-semibold text-gray-900">
                        {app.displayName}
                      </span>
                      <PurposeLine app={app} mode={mode} />
                    </AppInfoTooltip>
                    <span aria-hidden="true" className="h-2 w-2 flex-none animate-pulse rounded-full bg-emerald-500" />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </OnboardingLayout>
    );
  }

  // ── Ready-to-assemble: the resolved plan + confirm ──
  if (phase === 'ready-to-assemble') {
    return (
      <OnboardingLayout currentStep="complete" workspaceType={workspaceType} onboardingMode={layoutMode} wide>
        <div className="rounded-3xl bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5">
          <div className="grid lg:grid-cols-12">
            <aside className="relative overflow-hidden rounded-t-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-900 p-4 text-white sm:p-8 lg:col-span-4 lg:rounded-l-3xl lg:rounded-tr-none">
              <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
              <div className="relative">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">Final step</p>
                <div className="mt-5 hidden h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-900/40 sm:flex">
                  <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 7l8-4 8 4-8 4-8-4zm0 5l8 4 8-4M4 17l8 4 8-4" />
                  </svg>
                </div>
                <p className="mt-1.5 text-base font-semibold leading-snug sm:mt-5 sm:text-lg">One click and everything below switches on.</p>
                <dl className="mt-3 grid grid-cols-2 gap-2 sm:mt-6 sm:gap-3">
                  <div className="rounded-2xl bg-white/[0.07] px-3 py-2 ring-1 ring-white/10 sm:p-4">
                    <dt className="text-xs text-slate-300">Outcomes</dt>
                    <dd className="mt-0.5 text-2xl font-semibold sm:mt-1 sm:text-3xl">{outcomes.length}</dd>
                  </div>
                  <div className="rounded-2xl bg-white/[0.07] px-3 py-2 ring-1 ring-white/10 sm:p-4">
                    <dt className="text-xs text-slate-300">Apps</dt>
                    <dd className="mt-0.5 text-2xl font-semibold sm:mt-1 sm:text-3xl">{planApps.length}</dd>
                  </div>
                </dl>
                {trialDays > 0 && (
                  <div className="mt-3 flex items-start gap-2 border-t border-white/10 pt-3 sm:mt-6 sm:gap-3 sm:pt-6">
                    <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-emerald-400/20 text-emerald-300">
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <p className="text-xs text-slate-200 sm:text-sm">
                      Your {trialDays}-day free trial starts as soon as your workspace is assembled.
                    </p>
                  </div>
                )}
              </div>
            </aside>

            <section className="min-w-0 p-4 sm:p-8 lg:col-span-8">
              <div className="mb-4 sm:mb-6">
                <h1 className="text-xl font-semibold tracking-tight text-gray-900 sm:text-3xl">{copy.assembleHeading}</h1>
                <p className="mt-1 text-sm text-gray-500 sm:mt-1.5 sm:text-base">{copy.assembleSub}</p>
              </div>

              <div className="mb-5 sm:mb-7">
                <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 sm:mb-3 sm:text-xs">
                  {`What you're improving (${outcomes.length})`}
                </h2>
                <OutcomesSummary outcomes={outcomes} listId="complete-outcomes" />
              </div>

              <div>
                <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 sm:mb-3 sm:text-xs">
                  {`Apps we're switching on (${planApps.length})`}
                </h2>
                {/*
                  Phase 2.x-c PR-C1 — cards mirror PricingSummary's layout
                  so the last two onboarding screens read consistently.
                  Each app is introduced by name with its purpose beneath.
                */}
                {planApps.length > COLLAPSE_APPS_ABOVE && (
                  <AppsDisclosure
                    apps={planApps}
                    expanded={appsExpanded}
                    onToggle={() => setAppsExpanded((v) => !v)}
                    controlsId="assemble-apps"
                  />
                )}
                <div
                  id="assemble-apps"
                  hidden={planApps.length > COLLAPSE_APPS_ABOVE && !appsExpanded}
                  className={`${planApps.length > COLLAPSE_APPS_ABOVE && !appsExpanded ? 'hidden' : 'grid'} gap-2.5 sm:grid-cols-2 xl:grid-cols-3 ${
                    planApps.length > COLLAPSE_APPS_ABOVE ? 'mt-3' : ''
                  }`}
                >
                  {planApps.map((app) => (
                    <div
                      key={app.appId}
                      className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm transition-shadow hover:shadow-md"
                    >
                      <AppLogo appId={app.appId} displayName={app.displayName} iconUrl={app.iconUrl} />
                      <AppInfoTooltip
                        description={app.description}
                        className={`block min-w-0 text-left leading-tight ${
                          app.description
                            ? 'rounded focus:outline-none focus:ring-2 focus:ring-emerald-400'
                            : ''
                        }`}
                        testId={`assemble-app-${app.appId}`}
                      >
                        <span className="block text-sm font-semibold text-gray-900">
                          {app.displayName}
                        </span>
                        <PurposeLine app={app} mode={mode} />
                      </AppInfoTooltip>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          <div className="sticky bottom-0 z-10 flex flex-row items-center justify-between gap-3 rounded-b-3xl border-t border-gray-100 bg-white px-3 py-2 sm:px-8 sm:py-4">
            <button
              type="button"
              onClick={handleBack}
              className="px-4 py-2.5 font-medium text-gray-600 transition-colors hover:text-gray-900 sm:px-6"
            >
              Back
            </button>
            <button
              type="button"
              onClick={confirmAssembly}
              className="rounded-xl bg-emerald-600 px-5 py-2.5 font-semibold sm:px-7 sm:py-3 text-white shadow-lg shadow-emerald-600/25 transition-colors hover:bg-emerald-700"
            >
              {copy.confirmCta}
            </button>
          </div>
        </div>
      </OnboardingLayout>
    );
  }

  // ── Degraded: workspace exists but is not yet launchable ──
  if (phase === 'degraded') {
    return (
      <OnboardingLayout currentStep="complete" workspaceType={workspaceType} onboardingMode={layoutMode}>
        <div className="mx-auto max-w-lg rounded-3xl bg-white p-8 text-center shadow-2xl shadow-black/40 ring-1 ring-black/5 sm:p-10">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 ring-1 ring-amber-100">
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="mt-5 text-xl font-semibold tracking-tight text-gray-900">Your workspace is still settling</h1>
          <p className="mt-2 text-sm text-gray-500">
            Your workspace was created and your selections are saved. Its apps just need a moment
            to finish switching on.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={verifyWorkspace}
              className="rounded-xl bg-emerald-600 px-5 py-2.5 font-semibold sm:px-7 sm:py-3 text-white shadow-lg shadow-emerald-600/25 transition-colors hover:bg-emerald-700"
            >
              Check again
            </button>
            <button
              type="button"
              onClick={goHome}
              className="px-6 py-3 font-medium text-gray-600 transition-colors hover:text-gray-900"
            >
              Go to your workspace
            </button>
          </div>
        </div>
      </OnboardingLayout>
    );
  }

  // ── Failed but recoverable: provisioning did not complete ──
  if (phase === 'failed-recoverable') {
    return (
      <OnboardingLayout currentStep="complete" workspaceType={workspaceType} onboardingMode={layoutMode}>
        <div className="mx-auto max-w-lg rounded-3xl bg-white p-8 text-center shadow-2xl shadow-black/40 ring-1 ring-black/5 sm:p-10">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-600 ring-1 ring-red-100">
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h1 className="mt-5 text-xl font-semibold tracking-tight text-gray-900">Something went wrong</h1>
          <p className="mt-2 text-sm text-gray-500">
            {error || "We couldn't finish assembling your workspace. Your selections are safe."}
          </p>
          <button
            type="button"
            onClick={confirmAssembly}
            className="mt-7 rounded-xl bg-emerald-600 px-5 py-2.5 font-semibold sm:px-7 sm:py-3 text-white shadow-lg shadow-emerald-600/25 transition-colors hover:bg-emerald-700"
          >
            Try again
          </button>
        </div>
      </OnboardingLayout>
    );
  }

  // ── Ready: provisioned workspace + launcher ──
  const oidcContinue = continueUrl && isValidOidcContinueUrl(continueUrl) ? continueUrl : '';
  const defaultLaunchUrl =
    defaultApp?.launchable && defaultApp.launchUrl ? defaultApp.launchUrl : '';
  const primaryLaunch = oidcContinue || defaultLaunchUrl;
  // H-2A: route the default-app CTA through the OIDC launch flow when the
  // app is handoff-enabled. The onboarding `?continue=` OIDC continuation
  // (oidcContinue) is a separate flow and is never touched here.
  const oidcLaunchAppId =
    !oidcContinue && defaultLaunchUrl && defaultApp?.oidcLaunch === true
      ? defaultApp.appId
      : null;

  return (
    <OnboardingLayout currentStep="complete" workspaceType={workspaceType} onboardingMode={layoutMode} wide>
      <div className="overflow-hidden rounded-3xl bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5">
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-900 px-4 py-6 text-center text-white sm:px-10 sm:py-10">
          <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-emerald-500/25 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-emerald-400/10 blur-3xl" />
          <div className="relative">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-900/40 sm:h-16 sm:w-16">
              <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="mt-3 text-xl font-semibold tracking-tight sm:mt-5 sm:text-3xl">{copy.readyHeading}</h1>
            <p className="mx-auto mt-1.5 max-w-xl text-sm text-slate-300 sm:mt-2 sm:text-base">{copy.readySub}</p>
          </div>
        </div>

        <div className="p-4 sm:p-8">
          {workspaceApps.length > 0 && (
            <div className="mb-5 sm:mb-8">
              <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 sm:mb-3 sm:text-xs">
                {`Your apps (${workspaceApps.length})`}
              </h2>
              {/*
                Phase 2.x-c PR-C1 — same card layout as PricingSummary and
                the ready-to-assemble screen, so the completed-workspace
                view carries the enduring value into the launcher-adjacent
                summary.
              */}
              {workspaceApps.length > COLLAPSE_APPS_ABOVE && (
                <AppsDisclosure
                  apps={workspaceApps}
                  expanded={yourAppsExpanded}
                  onToggle={() => setYourAppsExpanded((v) => !v)}
                  controlsId="your-apps"
                  title={`${workspaceApps.length} apps switched on`}
                />
              )}
              <div
                id="your-apps"
                hidden={workspaceApps.length > COLLAPSE_APPS_ABOVE && !yourAppsExpanded}
                className={`${
                  workspaceApps.length > COLLAPSE_APPS_ABOVE && !yourAppsExpanded ? 'hidden' : 'grid'
                } gap-2.5 sm:grid-cols-2 xl:grid-cols-3 ${workspaceApps.length > COLLAPSE_APPS_ABOVE ? 'mt-3' : ''}`}
              >
                {workspaceApps.map((app) => (
                  <div
                    key={app.appId}
                    className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm"
                  >
                    <AppLogo appId={app.appId} displayName={app.displayName} iconUrl={app.iconUrl} />
                    <AppInfoTooltip
                      description={app.description}
                      className={`block min-w-0 text-left leading-tight ${
                        app.description
                          ? 'rounded focus:outline-none focus:ring-2 focus:ring-emerald-400'
                          : ''
                      }`}
                      testId={`your-app-${app.appId}`}
                    >
                      <span className="block text-sm font-semibold text-gray-900">
                        {app.displayName}
                      </span>
                      <PurposeLine app={app} mode={mode} />
                    </AppInfoTooltip>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mx-auto flex max-w-md flex-col gap-3">
            {primaryLaunch ? (
              <a
                href={primaryLaunch}
                onClick={(e) => {
                  if (!oidcLaunchAppId) return;
                  e.preventDefault();
                  void launchSuiteApp(oidcLaunchAppId, {
                    surface: 'assembly-ready',
                    fallbackUrl: defaultLaunchUrl,
                  });
                }}
                className="rounded-xl bg-emerald-600 px-8 py-3 text-center font-semibold sm:py-3.5 text-white shadow-lg shadow-emerald-600/25 transition-colors hover:bg-emerald-700"
              >
                {oidcContinue
                  ? 'Continue'
                  : `Open ${defaultApp?.displayName ?? 'your workspace'}`}
              </a>
            ) : null}
            <button
              type="button"
              onClick={goHome}
              className={
                primaryLaunch
                  ? 'px-8 py-3 font-medium text-gray-600 transition-colors hover:text-gray-900'
                  : 'rounded-xl bg-emerald-600 px-8 py-3.5 font-semibold text-white shadow-lg shadow-emerald-600/25 transition-colors hover:bg-emerald-700'
              }
            >
              Go to your workspace
            </button>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  );
}
